/**
 * Shared POS room status calculation and configuration.
 *
 * Single source of truth for:
 * - Tier frequencies (days between services)
 * - Tier metadata (labels, colors)
 * - Room compliance status calculation
 * - Status display config (colors, labels, icons)
 *
 * Used by: POSDashboard, POSRooms, POSServiceManagement, POSBatchServiceModal, POSGroupDashboard
 */

// ---------------------------------------------------------------------------
// Tier Configuration
// ---------------------------------------------------------------------------

export const POS_GOLD = '#BD982F';
export const POS_GOLD_DARK = '#8B7226';

/** Days between services per tier */
export const TIER_FREQUENCY: Record<string, number> = {
  signature: 120,
  distinguished: 180,
  assured: 365,
};

/** Tier display metadata */
export const TIER_CONFIG: Record<string, { label: string; color: string; frequency: number }> = {
  signature: { label: 'Signature', color: POS_GOLD, frequency: 120 },
  distinguished: { label: 'Distinguished', color: '#A8A9AD', frequency: 180 },
  assured: { label: 'Assured', color: '#CD7F32', frequency: 365 },
};

export const TIER_LABELS: Record<string, string> = {
  signature: 'Signature (3× per year)',
  distinguished: 'Distinguished (2× per year)',
  assured: 'Assured (1× per year)',
};

// ---------------------------------------------------------------------------
// Room Compliance Status
// ---------------------------------------------------------------------------

export type RoomStatus = 'certified' | 'due-soon' | 'overdue' | 'not-serviced';

/** Window (days) before due date that status changes to "due-soon" */
export const DUE_SOON_WINDOW_DAYS = 30;

/** Room status display config */
export const ROOM_STATUS_CONFIG: Record<RoomStatus, { label: string; color: string; bg: string }> = {
  'certified': { label: 'Certified', color: 'var(--color-success)', bg: 'var(--color-success-bg)' },
  'due-soon': { label: 'Due Soon', color: 'var(--color-warning)', bg: 'var(--color-warning-bg)' },
  'overdue': { label: 'Overdue', color: 'var(--color-error)', bg: 'var(--color-error-bg)' },
  'not-serviced': { label: 'Not Serviced', color: 'var(--color-text-light)', bg: 'var(--color-border)' },
};

/** Extended status config for matrix view (includes booked + future states) */
export const MATRIX_STATUS_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  'not-serviced': { label: 'Not Serviced', color: 'var(--color-warning)', icon: '○' },
  'booked': { label: 'Booked', color: '#8b5cf6', icon: '📅' },
  'certified': { label: 'Certified', color: 'var(--color-success)', icon: '✓' },
  'due-soon': { label: 'Due Soon', color: 'var(--color-warning)', icon: '⚠' },
  'overdue': { label: 'Overdue', color: 'var(--color-error)', icon: '✕' },
  'future': { label: 'Future', color: 'var(--color-border)', icon: '→' },
};

// ---------------------------------------------------------------------------
// Service Request Status
// ---------------------------------------------------------------------------

export type RequestStatus = 'pending' | 'confirmed' | 'completed' | 'cancelled';

export const REQUEST_STATUS_CONFIG: Record<RequestStatus, { label: string; color: string; bg: string }> = {
  pending: { label: 'Pending', color: 'var(--color-warning)', bg: 'var(--color-warning-bg)' },
  confirmed: { label: 'Confirmed', color: 'var(--color-info)', bg: 'var(--color-info-bg)' },
  completed: { label: 'Completed', color: 'var(--color-success)', bg: 'var(--color-success-bg)' },
  cancelled: { label: 'Cancelled', color: 'var(--color-text-light)', bg: 'var(--color-border)' },
};

// ---------------------------------------------------------------------------
// Room Types
// ---------------------------------------------------------------------------

export const ROOM_TYPES = [
  { value: 'king', label: 'King' },
  { value: 'queen', label: 'Queen' },
  { value: 'double', label: 'Double' },
  { value: 'single', label: 'Single' },
];

// ---------------------------------------------------------------------------
// Calculation Functions
// ---------------------------------------------------------------------------

/**
 * Calculate room compliance status based on last service date and property tier.
 *
 * @param lastServiceDate - ISO date string or null
 * @param tier - Hospitality tier (signature, distinguished, assured)
 * @returns RoomStatus
 */
export function calculateRoomStatus(
  lastServiceDate: string | null | undefined,
  tier: string,
): RoomStatus {
  if (!lastServiceDate) return 'not-serviced';

  const frequency = TIER_FREQUENCY[tier] ?? 365;
  const lastService = new Date(lastServiceDate);
  const today = new Date();
  const daysSince = Math.floor((today.getTime() - lastService.getTime()) / 86400000);
  const daysUntilDue = frequency - daysSince;

  if (daysUntilDue < 0) return 'overdue';
  if (daysUntilDue <= DUE_SOON_WINDOW_DAYS) return 'due-soon';
  return 'certified';
}

/**
 * Calculate days until next service is due.
 * Negative = overdue by N days. Positive = N days remaining.
 */
export function daysUntilServiceDue(
  lastServiceDate: string | null | undefined,
  tier: string,
): number | null {
  if (!lastServiceDate) return null;

  const frequency = TIER_FREQUENCY[tier] ?? 365;
  const lastService = new Date(lastServiceDate);
  const today = new Date();
  const daysSince = Math.floor((today.getTime() - lastService.getTime()) / 86400000);
  return frequency - daysSince;
}

/**
 * Get the display config for a room status.
 */
export function getRoomStatusConfig(status: RoomStatus) {
  return ROOM_STATUS_CONFIG[status] || ROOM_STATUS_CONFIG['not-serviced'];
}
